import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import ConfigurationPage from '@express/components/pages/ConfigurationPage.jsx'
import useConfigurationData from '../hooks/useConfigurationData.js'
import { getDeviceInfo } from '../utils/firestoreAdapter.js'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useDevicePermission } from '../hooks/useDevicePermission.js'
import LoadingSpinner from '../components/LoadingSpinner.jsx'
import batteryConfigurations from '@express-root/batteryConfigurations.json'

// Global state to track current device ID
let currentDeviceId = null
// Global state to store our Firestore configuration data
let cachedConfigData = null
// Global state to store the saveConfiguration function from our hook
let globalSaveConfiguration = null

/**
 * Configuration page wrapper that adapts Firestore data to work with the express ConfigurationPage component
 * This provides a seamless integration without modifying the original component
 * Also hides the Wi-Fi/Ethernet section and Revatek Connect registration for cloud configuration
 * Passes QueryClient explicitly for production build compatibility
 */
function Configuration() {
  // Get selected device from cloud context
  const { selectedDeviceId } = useCloudContext()
  const queryClient = useQueryClient() // Get QueryClient from context
  
  // Get user permissions for the selected device
  const { canWrite } = useDevicePermission(selectedDeviceId)
  
  // Track device connection status
  const [deviceNotConnected, setDeviceNotConnected] = React.useState(false)
  const [checkingConnection, setCheckingConnection] = React.useState(false)
  
  // Update the global device ID immediately (synchronously)
  currentDeviceId = selectedDeviceId
  
  // Use our Firestore-based hook that provides the same interface as the express version
  const configurationData = useConfigurationData()

  // Component-scoped fetch override with proper cleanup
  React.useEffect(() => {
    const originalFetch = window.fetch

    // Override fetch for this component only
    window.fetch = async (url, options) => {
      // Intercept all local API calls
      if (typeof url === 'string' && url.startsWith('/')) {
    
    if (url === '/getDeviceInfo') {
      if (!currentDeviceId) {
        // Return empty device info instead of error
        return {
          ok: true,
          json: async () => ({}),
          status: 200,
          statusText: 'OK',
        }
      }

      try {
        const deviceInfo = await getDeviceInfo(currentDeviceId)
        if (!deviceInfo) {
          // Return empty device info instead of error
          return {
            ok: true,
            json: async () => ({}),
            status: 200,
            statusText: 'OK',
          }
        }
        
        return {
          ok: true,
          json: async () => deviceInfo,
          status: 200,
          statusText: 'OK',
        }
      } catch (_error) {
        // Return empty device info instead of error to prevent console errors
        return {
          ok: true,
          json: async () => ({}),
          status: 200,
          statusText: 'OK',
        }
      }
    }
    
    if (url === '/config') {
      // Handle both GET and POST requests
      if (options?.method === 'POST') {
        // Handle configuration save
        if (!currentDeviceId) {
          return {
            ok: false,
            status: 400,
            statusText: 'Bad Request',
            json: async () => ({ error: 'No device selected' }),
          }
        }

        if (!globalSaveConfiguration) {
          return {
            ok: false,
            status: 500,
            statusText: 'Internal Server Error',
            json: async () => ({ error: 'Save function not initialized' }),
          }
        }

        try {
          // Parse the request body
          const configData = typeof options.body === 'string' 
            ? JSON.parse(options.body) 
            : options.body

          // Use our Firestore-based save function
          const savedData = await globalSaveConfiguration(configData)
          
          // Update cached data for subsequent GET requests
          cachedConfigData = savedData

          return {
            ok: true,
            json: async () => savedData,
            status: 200,
            statusText: 'OK',
          }
        } catch (saveError) {
          console.error('❌ Configuration save failed:', saveError)
          return {
            ok: false,
            status: 500,
            statusText: 'Internal Server Error',
            json: async () => ({ error: 'Failed to save configuration' }),
          }
        }
      } else {
        // Handle GET request (existing logic)
        if (cachedConfigData) {
          return {
            ok: true,
            json: async () => cachedConfigData,
            status: 200,
            statusText: 'OK',
          }
        }
        
        // Return empty config if no cached data
        return {
          ok: true,
          json: async () => ({}),
          status: 200,
          statusText: 'OK',
        }
      }
    }
    
    if (url === '/batConfig') {
      // Return real battery configurations
      return {
        ok: true,
        json: async () => batteryConfigurations,
        status: 200,
        statusText: 'OK',
      }
    }
    
    if (url === '/getNetworkInfo') {
      // Return mock network information
      const mockNetworkInfo = {
        interfaces: [
          {
            name: 'eth0',
            type: 'Ethernet',
            status: 'connected',
            ipAddress: '192.168.1.100',
            netmask: '255.255.255.0',
            gateway: '192.168.1.1',
            macAddress: '00:1B:44:11:3A:B7',
          },
          {
            name: 'wlan0',
            type: 'WiFi',
            status: 'disconnected',
            ipAddress: null,
            netmask: null,
            gateway: null,
            macAddress: '02:1B:44:11:3A:B8',
            ssid: null,
            signalStrength: null,
          },
        ],
        dns: ['8.8.8.8', '8.8.4.4'],
        hostname: `altion-${currentDeviceId}`,
        connectivity: {
          internet: true,
          localNetwork: true,
        },
      }
      
      return {
        ok: true,
        json: async () => mockNetworkInfo,
        status: 200,
        statusText: 'OK',
      }
    }
    
    // Catch-all for API routes to prevent 404s from other pages (like Dashboard)
    if (url.startsWith('/api') || url.startsWith('/faults')) {
      return {
        ok: true,
        json: async () => [],
        status: 200,
        statusText: 'OK',
      }
    }
    
    // Intercept any other local API calls and return appropriate responses
    return {
      ok: false,
      status: 404,
      statusText: 'Not Found',
      json: async () => ({ error: 'Endpoint not found in cloud environment' }),
    }
  }
  
  // Use original fetch for all other requests (external URLs, Firebase, etc.)
  return originalFetch(url, options)
}

    // Cleanup: restore original fetch when component unmounts
    return () => {
      window.fetch = originalFetch
    }
  }, []) // Empty dependency array - only run once on mount/unmount

  // Function to check device connection by refetching data
  const checkDeviceConnection = React.useCallback(async () => {
    if (!selectedDeviceId) return
    
    setCheckingConnection(true)
    try {
      // Force refetch of configuration data
      if (configurationData.refetch) {
        await configurationData.refetch()
      }
    } catch (error) {
      console.error('Error checking device connection:', error)
    } finally {
      setCheckingConnection(false)
    }
  }, [selectedDeviceId, configurationData])

  // Simple device connection check based on configuration data
  React.useEffect(() => {
    if (selectedDeviceId && !configurationData.isLoading) {
      // Check if device has any meaningful configuration data
      // Look for cvVolts > 0 as indicator of device connection
      const hasConfigData = configurationData.formData && 
        configurationData.formData.cvVolts && 
        configurationData.formData.cvVolts > 0
      
      setDeviceNotConnected(!hasConfigData)
    } else {
      // Reset when no device selected or still loading
      setDeviceNotConnected(false)
    }
  }, [selectedDeviceId, configurationData.isLoading, configurationData.formData])

  // Make the saveConfiguration function globally available for the fetch override
  React.useEffect(() => {
    globalSaveConfiguration = configurationData.saveConfiguration
  }, [configurationData.saveConfiguration])

  // Dynamically calculate and set the left navigation width
  React.useEffect(() => {
    const updateLeftNavWidth = () => {
      const leftNavElement = document.querySelector('.bg-light.border-end')
      if (leftNavElement) {
        const width = leftNavElement.offsetWidth
        document.documentElement.style.setProperty('--left-nav-width', `${width}px`)
      } else {
        // Fallback: no left nav visible (e.g., fleet view)
        document.documentElement.style.setProperty('--left-nav-width', '0px')
      }
    }

    // Update immediately
    updateLeftNavWidth()

    // Update on window resize
    const handleResize = () => updateLeftNavWidth()
    window.addEventListener('resize', handleResize)

    // Use a MutationObserver to detect when left nav appears/disappears
    const observer = new MutationObserver(updateLeftNavWidth)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    return () => {
      window.removeEventListener('resize', handleResize)
      observer.disconnect()
    }
  }, [selectedDeviceId]) // Re-run when device changes (might affect nav visibility)

  // Hide the WifiEthernetSection and RevatekConnectRegistration components after component mounts
  React.useEffect(() => {
    const hideSections = () => {
      // Find the WifiEthernetSection component by its data attribute
      const wifiSection = document.querySelector('[data-component="WifiEthernetSection"]')
      if (wifiSection) {
        wifiSection.style.display = 'none'
      }
      
      // Find the RevatekConnectRegistration component by its data attribute
      const registrationSection = document.querySelector('[data-component="RevatekConnectRegistration"]')
      if (registrationSection) {
        registrationSection.style.display = 'none'
      }
    }

    // Hide immediately if components are already present
    hideSections()

    // Set up a MutationObserver to hide the sections when they get added to the DOM
    const observer = new MutationObserver(() => {
      hideSections()
    })

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    // Cleanup observer on unmount
    return () => observer.disconnect()
  }, [])

  // Cache the configuration data for the express hook to use
  React.useEffect(() => {
    // Always update cached data when formData changes, regardless of loading state
    if (configurationData.formData) {
      // Fix for validation error: When Wi-Fi section is hidden but networkType requires Wi-Fi credentials,
      // we need to ensure the hidden fields have valid values to prevent validation errors
      if (configurationData.formData.networkType && configurationData.formData.networkType !== 'ethernet') {
        // Set default values for hidden Wi-Fi fields to prevent validation errors
        const fixedFormData = {
          ...configurationData.formData,
          networkName: configurationData.formData.networkName || 'cloud-managed',
          regulatorPassword: configurationData.formData.regulatorPassword || 'cloud-managed',
        }
        cachedConfigData = fixedFormData
      } else {
        cachedConfigData = configurationData.formData
      }
      
      // Force express component to refetch data by invalidating React Query cache
      if (window.queryClient) {
        window.queryClient.invalidateQueries({ queryKey: ['configuration'] })
      }
      
      // Also dispatch a custom event that the express component can listen to as backup
      const refreshEvent = new CustomEvent('configurationDataRefresh', {
        detail: { deviceId: selectedDeviceId, data: cachedConfigData },
      })
      window.dispatchEvent(refreshEvent)
    }
  }, [configurationData.formData, selectedDeviceId])

  // Update the global device ID whenever it changes (backup)
  React.useEffect(() => {
    if (selectedDeviceId !== currentDeviceId) {
      currentDeviceId = selectedDeviceId
    }
  }, [selectedDeviceId])

  // Don't render ConfigurationPage until we have a device ID
  if (!selectedDeviceId) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Please select a device to configure</h3>
        <p>Use the device selector in the top navigation to choose a device.</p>
      </div>
    )
  }

  // Wait for configuration data to load before rendering
  if (configurationData.isLoading) {
    return <LoadingSpinner message="Loading configuration..." />
  }
  
  // Show device not connected state when device exists but has no configuration data
  if (deviceNotConnected) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-wifi-off" style={{ fontSize: '4rem', color: 'var(--text-light-muted)' }} />
        <h4 className="mt-3 mb-3">Device Not Connected</h4>
        <p className="text-muted mb-4">
          The device <strong>{selectedDeviceId}</strong> hasn&apos;t uploaded any configuration data to the cloud.
        </p>
        <div className="text-muted">
          <p className="mb-2"><strong>Troubleshooting:</strong></p>
          <ul className="list-unstyled">
            <li>• Check that the device is powered on</li>
            <li>• Verify internet/cellular connectivity</li>
            <li>• Ensure the device is properly configured</li>
            <li>• Allow time for initial configuration to be uploaded</li>
          </ul>
        </div>
        <button 
          className="btn btn-outline-primary mt-3"
          onClick={checkDeviceConnection}
          disabled={checkingConnection}
        >
          <i className={`bi ${checkingConnection ? 'bi-arrow-clockwise spin' : 'bi-arrow-clockwise'} me-2`} />
          {checkingConnection ? 'Checking...' : 'Check Connection Again'}
        </button>
      </div>
    )
  }

  // Check if there's an error in the configuration data
  if (configurationData.error) {
    return (
      <div className="alert alert-danger" style={{ margin: '20px' }}>
        <i className="bi bi-exclamation-triangle me-2" />
        Failed to load configuration: {configurationData.error}
      </div>
    )
  }

  // Let the ConfigurationPage call its own hook - we've overridden the endpoints it needs
  return (
    <>
      <style>
        {`
          /* Adjust the sticky footer to not overlap left navigation */
          .position-sticky.bottom-0 {
            position: fixed !important;
            bottom: 0 !important;
            left: var(--left-nav-width, 260px) !important;
            right: 0 !important;
            z-index: 1000 !important;
            ${!canWrite ? 'display: none !important;' : ''}
          }
          
          /* Reset the container inside the footer to full width */
          .position-sticky.bottom-0 .container {
            max-width: none !important;
            margin: 0 !important;
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
          
          /* Spinning animation for refresh icon */
          .spin {
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <ConfigurationPage queryClient={queryClient} />
    </>
  )
}

export default Configuration
